/**
 * Brand configuration — the white-label API.
 *
 * DEFAULT_BRAND holds the values shipped in code. BRAND is the effective
 * configuration: defaults plus any overrides from the loaded content pack
 * (see content.js `applyContentPack`). Consumers keep importing BRAND —
 * it is mutated in place so existing references stay valid.
 *
 * cssOverrides: any CSS custom property from tokens.css can be overridden.
 * They are injected into a <style> element by applyBrand().
 */
export const DEFAULT_BRAND = Object.freeze({
  simName: 'MedVance Group Leadership Simulation',
  companyShort: 'MEDVANCE',
  contactLabel: 'Move the Machine',
  contactEmail: 'hayley@movethemachine.com.au',

  /** Named as the rights holder in the on-screen and printed copyright notices. */
  copyrightHolder: 'Move the Machine',

  /** @type {Record<string, string>} */
  cssOverrides: {
    // '--brand-primary': '#ff6b00',
    // '--surface-navy': '#1a1a2e',
  },
});

export const BRAND = { ...DEFAULT_BRAND, cssOverrides: { ...DEFAULT_BRAND.cssOverrides } };

/**
 * Overlay content-pack brand values onto the defaults. Only recognised keys
 * are applied; empty strings are ignored so a blank editor field means
 * "use the default". Passing null/undefined restores the defaults.
 * @param {object|null|undefined} overrides
 */
export function applyBrandOverrides(overrides) {
  const next = { ...DEFAULT_BRAND, cssOverrides: { ...DEFAULT_BRAND.cssOverrides } };
  if (overrides && typeof overrides === 'object') {
    for (const k of ['simName', 'companyShort', 'contactLabel', 'contactEmail', 'copyrightHolder']) {
      if (typeof overrides[k] === 'string' && overrides[k].trim()) next[k] = overrides[k].trim();
    }
    if (overrides.cssOverrides && typeof overrides.cssOverrides === 'object') {
      for (const [prop, val] of Object.entries(overrides.cssOverrides)) {
        if (prop.startsWith('--') && typeof val === 'string' && val.trim()) {
          next.cssOverrides[prop] = val.trim();
        }
      }
    }
  }
  Object.keys(BRAND).forEach(k => delete BRAND[k]);
  Object.assign(BRAND, next);
}

/** Copyright line shown in the app footer and on printed/exported deliverables. */
export const copyrightLine = () =>
  `© ${new Date().getFullYear()} ${BRAND.copyrightHolder}. All rights reserved.`;

/** Idempotent — safe to call again after a content pack loads or is edited. */
export function applyBrand() {
  document.title = BRAND.simName;
  const footer = document.getElementById('app-footer');
  if (footer) footer.textContent = `${BRAND.simName} · ${copyrightLine()}`;
  document.getElementById('brand-overrides')?.remove();
  const rules = Object.entries(BRAND.cssOverrides)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n');
  if (!rules) return;
  const el = document.createElement('style');
  el.id = 'brand-overrides';
  el.textContent = `:root {\n${rules}\n}`;
  document.head.appendChild(el);
}
